import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { CommonService } from '@services/common.service';
import { SecureStorageService } from '@services/securestorage.service';

interface RouteAccess {
  routerLink: string;
  roles: string[];
  isExternalUser: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RBACGuard implements CanActivate {
  currentRoute: string;

  private readonly standardRoles = ['Viewer', 'Admin', 'Owner', 'Editor'];
  private readonly adminOwnerRoles = ['Admin', 'Owner'];
  private readonly superAdminRoles = ['SuperAdmin'];
  private readonly iporRoles = [
    'Viewer',
    'Contributor',
    'Functional Lead',
    'Instance Admin',
    'Instance Owner',
    'Reviewer',
  ];
  private readonly iporNonViewerRoles = [
    'Contributor',
    'Functional Lead',
    'Instance Admin',
    'Instance Owner',
    'Reviewer',
  ];
  private readonly workspaceModes = ['Archives', 'Favorites', 'All'];

  private isIPORModuleSafe(): boolean {
    const checker = (this.commonService as any)?.isIPORModule;
    return typeof checker === 'function'
      ? !!checker.call(this.commonService)
      : false;
  }

  constructor(
    private readonly commonService: CommonService,
    private readonly storageService: SecureStorageService,
    private readonly router: Router,
  ) {}

  private createRoute(
    routerLink: string,
    roles: string[],
    isExternalUser: boolean,
  ): RouteAccess {
    return { routerLink, roles, isExternalUser };
  }

  routeList: RouteAccess[] = [
    this.createRoute('', this.standardRoles, true),
    this.createRoute('user-management', this.adminOwnerRoles, false),
    this.createRoute('approval-request', this.superAdminRoles, false),
    this.createRoute('approval-request/:module', this.superAdminRoles, false),
    this.createRoute('my-workspace/:mode', this.standardRoles, true),
    ...this.workspaceModes.map((mode) =>
      this.createRoute(`my-workspace/${mode}`, this.standardRoles, true),
    ),
    this.createRoute('project-detail/:id', this.standardRoles, true),
    this.createRoute('project-detail/:module/:id', this.standardRoles, true),
    this.createRoute('project-detail-form', this.standardRoles, false),
    this.createRoute('project-detail-form/:id', ['Admin'], false),
    this.createRoute(
      'project-detail/:module/:id/:componentid/:type',
      this.standardRoles,
      false,
    ),
    this.createRoute('onedb-upload', ['Admin'], false),
  ];

  iporrouteList: RouteAccess[] = [
    this.createRoute('', this.iporRoles, true),
    this.createRoute('project-detail-form', this.iporRoles, false),
    this.createRoute('project-detail-form/:id', ['Instance Admin'], false),
    this.createRoute('user-access-management', this.iporNonViewerRoles, false),
    this.createRoute(
      'user-access-management/:id',
      this.iporNonViewerRoles,
      false,
    ),
    this.createRoute('approval-request', this.superAdminRoles, false),
    this.createRoute('approval-request/:module', this.superAdminRoles, false),
    this.createRoute('my-workspace/:mode', this.iporRoles, false),
    ...this.workspaceModes.map((mode) =>
      this.createRoute(`my-workspace/${mode}`, this.iporRoles, false),
    ),
    this.createRoute('assessment-detail/:id', this.iporRoles, false),
    this.createRoute('assessment-detail/:module/:id', this.iporRoles, false),
    this.createRoute('onedb-upload', this.superAdminRoles, false),
  ];

  canActivate(route: ActivatedRouteSnapshot): boolean {
    let valid: boolean = false;
    let userInfo = this.commonService.getUserInfo();
    if (!userInfo) {
      return false;
    }
    if (userInfo?.IsAdmin) {
      return true;
    }
    let routeList = this.isIPORModuleSafe()
      ? this.iporrouteList
      : this.routeList;
    const routePath = route.routeConfig?.path;
    if (routePath == null) {
      return false;
    }
    this.currentRoute = routePath;
    let accessRolesList = this.commonService.getAccessibleRoles();
    let matchedRoute = routeList.find((x) => x.routerLink == this.currentRoute);
    const isExternal = this.commonService.isExternalUser();
    if (!matchedRoute?.roles?.length) {
      return false;
    }
    let roleList = matchedRoute.roles;
    if (isExternal && !matchedRoute.isExternalUser) {
      this.router.navigate(['/']);
      return false;
    }
    if (accessRolesList != null && accessRolesList.length > 0) {
      for (const role of accessRolesList) {
        if (roleList.includes(role.RoleName)) {
          valid = true;
          break;
        }
      }
    }
    return valid;
  }
}
